/**
 * Tenants API — superadmin-only operations against /auth/superadmin/tenants
 * and per-tenant shuttle settings against /shuttle/v1/admin/tenants/{slug}/settings.
 *
 * @module api/tenants
 */
import { apiFetch } from "../auth.ts";

export interface Tenant {
	id: string;
	slug: string;
	name: string;
	is_root: boolean;
	created_at: string;
	/** Enabled platform capabilities, e.g. ["fleet","shuttle"]. */
	capabilities?: string[];
	/** Number of users belonging to this tenant. */
	user_count?: number;
}

// ── Auth-svc ride tenants (/auth/superadmin/tenants) ─────────────────────────

const BASE = "/auth/superadmin/tenants";

export async function listTenants(): Promise<Tenant[]> {
	const res = await apiFetch(BASE);
	if (!res.ok) throw new Error(await res.text());
	const body = await res.json();
	return body.tenants ?? [];
}

export async function createTenant(
	slug: string,
	name: string,
): Promise<Tenant> {
	const res = await apiFetch(BASE, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ slug, name }),
	});
	if (!res.ok) throw new Error(await res.text());
	return res.json();
}

/**
 * Rename a tenant, in both of the registries that hold its name.
 *
 * A tenant's display name is stored twice: auth-svc's `tenants` row, which the
 * tenant selector and the session read, and shuttle-svc's `shuttle.tenants`
 * row, which every fleet-side reader takes it from - including the operations
 * report, the one artefact that leaves the company. Renaming only the first
 * changed the name everywhere an operator looks except the document they hand
 * a client, and nothing anywhere surfaced the divergence: an operator renamed
 * to "Term" kept exporting reports titled "Emaar App Marasi".
 *
 * The slug is immutable and comes back on the auth response, which is what
 * addresses the fleet-side row - so the rename needs no extra argument.
 */
export async function updateTenant(id: string, name: string): Promise<Tenant> {
	const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ name }),
	});
	if (!res.ok) throw new Error(await res.text());
	const tenant: Tenant = await res.json();
	// Reported, never swallowed. A half-applied rename is precisely the state
	// that produced a report titled with a name the operator had already
	// changed, so it has to be visible at the moment it happens rather than
	// discovered in a document months later.
	try {
		await patchShuttleSettings(tenant.slug, { name });
	} catch (err) {
		const detail = err instanceof Error ? err.message : String(err);
		throw new Error(
			`Renamed for sign-in and the tenant selector, but the fleet-side name ` +
				`could not be updated, so reports and fleet screens will keep the ` +
				`old one: ${detail}`,
		);
	}
	return tenant;
}

export async function deleteTenant(id: string): Promise<void> {
	const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
	});
	if (!res.ok) throw new Error(await res.text());
}

/** Read a tenant's enabled capabilities (e.g. ["fleet","shuttle"]). */
export async function getTenantCapabilities(
	tenantId: string,
): Promise<string[]> {
	const res = await apiFetch(
		`${BASE}/${encodeURIComponent(tenantId)}/capabilities`,
	);
	if (!res.ok) throw new Error(await res.text());
	const body = await res.json();
	return body.capabilities ?? [];
}

/** Enable or disable a single capability for a tenant. */
export async function setTenantCapability(
	tenantId: string,
	capability: string,
	enabled: boolean,
): Promise<void> {
	const res = await apiFetch(
		`${BASE}/${encodeURIComponent(tenantId)}/capabilities/${encodeURIComponent(capability)}`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ enabled }),
		},
	);
	if (!res.ok) throw new Error(await res.text());
}

// ── Per-tenant capability config (JSONB) ─────────────────────────────────────
// Each capability ("fleet", "shuttle", …) carries a free-form JSON config blob.
// The 'fleet' capability holds the IOT/VTU hardware settings consumed by the
// IOT Platform Settings page.

/** Read a capability's JSON config object (returns {} when unset). */
export async function getCapabilityConfig(
	tenantId: string,
	capability: string,
): Promise<Record<string, unknown>> {
	const res = await apiFetch(
		`${BASE}/${encodeURIComponent(tenantId)}/capabilities/${encodeURIComponent(capability)}/config`,
	);
	if (!res.ok) throw new Error(await res.text());
	return (await res.json()) ?? {};
}

/** Replace a capability's JSON config object. */
export async function setCapabilityConfig(
	tenantId: string,
	capability: string,
	config: Record<string, unknown>,
): Promise<void> {
	const res = await apiFetch(
		`${BASE}/${encodeURIComponent(tenantId)}/capabilities/${encodeURIComponent(capability)}/config`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(config),
		},
	);
	if (!res.ok) throw new Error(await res.text());
}

// ── Per-tenant shuttle settings (/shuttle/v1/admin/tenants/{slug}/settings) ──
// The shuttle-svc row is auto-provisioned on first admin touch.

const SHUTTLE_ADMIN_BASE = "/shuttle/v1/admin/tenants";

export interface ShuttleSettings {
	id: string;
	slug: string;
	name: string;
	is_root: boolean;
	map_center_lat: number;
	map_center_lng: number;
	map_default_zoom: number;
	daily_eta_budget: number;
	daily_tile_budget: number;
	map_bounds_sw_lat: number | null;
	map_bounds_sw_lng: number | null;
	map_bounds_ne_lat: number | null;
	map_bounds_ne_lng: number | null;
}

export interface ShuttleSettingsUpdate {
	name?: string;
	map_center_lat?: number;
	map_center_lng?: number;
	map_default_zoom?: number;
	daily_eta_budget?: number;
	daily_tile_budget?: number;
	/** Set all four to set bounds. Omit all four to leave existing bounds untouched. */
	map_bounds_sw_lat?: number;
	map_bounds_sw_lng?: number;
	map_bounds_ne_lat?: number;
	map_bounds_ne_lng?: number;
	/** Set true to remove bounds; takes priority over the four bound fields. */
	clear_bounds?: boolean;
}

export async function getShuttleSettings(
	slug: string,
): Promise<ShuttleSettings> {
	const res = await apiFetch(
		`${SHUTTLE_ADMIN_BASE}/${encodeURIComponent(slug)}/settings`,
	);
	if (!res.ok) throw new Error(await res.text());
	return res.json();
}

export async function patchShuttleSettings(
	slug: string,
	upd: ShuttleSettingsUpdate,
): Promise<ShuttleSettings> {
	const res = await apiFetch(
		`${SHUTTLE_ADMIN_BASE}/${encodeURIComponent(slug)}/settings`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(upd),
		},
	);
	if (!res.ok) throw new Error(await res.text());
	return res.json();
}
